import { GridLegacy, Stack, Typography, Card } from '@mui/material';
import { Icon as IconType } from '@tabler/icons-react';
import { Controller, Control } from 'react-hook-form';
import { useTheme } from '@mui/material/styles';
import { RegularBreakpoints } from '@mui/material/GridLegacy';

export type ClickableBoxOptionProps = {
  id: string | null;
  label: string;
  description: string;
  Icon: IconType;
};

interface HookFormClickableBoxProps {
  name: string;
  control: Control<any>;
  options: (Omit<ClickableBoxOptionProps, 'description'> & {
    description?: string;
  })[];
  gridProps?: {
    xs?: RegularBreakpoints['xs'];
    md?: RegularBreakpoints['md'];
  };
  disabled?: boolean;
}

export const HookFormClickableBox = ({
  name,
  control,
  options,
  gridProps = { xs: 12, md: 2 },
  disabled,
}: HookFormClickableBoxProps) => {
  const theme = useTheme();

  const renderBoxContent = (
    option: Omit<ClickableBoxOptionProps, 'description'> & {
      description?: string;
    }
  ) => {
    if (option.description) {
      return (
        <GridLegacy container alignItems="center">
          <GridLegacy item xs={2} container justifyContent="center">
            <option.Icon size={28} />
          </GridLegacy>
          <GridLegacy item xs={10} sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
              {option.label}
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
              {option.description}
            </Typography>
          </GridLegacy>
        </GridLegacy>
      );
    }

    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{ height: '100%', p: 2 }}
      >
        <option.Icon size={32} />
        <Typography variant="subtitle1" align="center" sx={{ fontWeight: 500 }}>
          {option.label}
        </Typography>
      </Stack>
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const columnsPerRow = 12 / (Number(gridProps?.md) ?? 2);
        return (
          <Card
            variant="outlined"
            sx={{ border: `1px solid ${theme.palette.grey[300]}` }}
          >
            <GridLegacy
              container
              direction="row"
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columnsPerRow}, 1fr)`,
                overflow: 'hidden',
              }}
            >
              {options.map((option, index) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => onChange(option.id)}
                  disabled={disabled}
                  style={{
                    cursor: disabled ? 'inherit' : 'pointer',
                    height: '100%',
                    border: 'none',
                    padding: 0,
                    borderRight:
                      index < options.length - 1
                        ? `1px solid ${theme.palette.grey[300]}`
                        : 'none',
                    borderBottom:
                      index < options.length - columnsPerRow
                        ? `1px solid ${theme.palette.grey[300]}`
                        : 'none',
                    backgroundColor:
                      value === option.id
                        ? theme.palette.primary.light
                        : theme.palette.background.paper,
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  {renderBoxContent(option)}
                </button>
              ))}
            </GridLegacy>
          </Card>
        );
      }}
    />
  );
};
